const STORAGE_KEY = 'wassiniCart';
const CART_EVENT = 'cart:update';

const defaultCart = {
  items: [],
  promoCode: null,
  deliveryCharge: 0,
};

const safeParse = (raw) => {
  try {
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !Array.isArray(parsed.items)
    ) {
      return { ...defaultCart };
    }
    return {
      ...defaultCart,
      ...parsed,
      items: Array.isArray(parsed.items) ? parsed.items : [],
    };
  } catch {
    return { ...defaultCart };
  }
};

const getSession = () => {
  if (typeof window === 'undefined') return { ...defaultCart };
  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  return raw ? safeParse(raw) : { ...defaultCart };
};

const persist = (cart) => {
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    window.dispatchEvent(
      new CustomEvent(CART_EVENT, {
        detail: cart,
      })
    );
  }
  return cart;
};

const findLineIndex = (items, productId, variantId) =>
  items.findIndex(
    (item) =>
      String(item.productId) === String(productId) &&
      String(item.variantId) === String(variantId)
  );

export const getCart = () => getSession();

export const getCartCount = () =>
  getSession().items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

export const subscribeToCart = (callback) => {
  if (typeof window === 'undefined') return () => {};
  const handler = (event) => {
    callback(event?.detail || getSession());
  };
  window.addEventListener(CART_EVENT, handler);
  return () => window.removeEventListener(CART_EVENT, handler);
};

const ensureQuantity = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return 1;
  return Math.floor(num);
};

export const addItemToCart = (payload) => {
  if (!payload?.productId) return getSession();
  const cart = getSession();
  const items = [...cart.items];
  const productId = payload.productId;
  const variantId = payload.variantId ?? 'default';
  const index = findLineIndex(items, productId, variantId);

  if (index >= 0) {
    items[index] = {
      ...items[index],
      quantity:
        Number(items[index].quantity || 1) + ensureQuantity(payload.quantity || 1),
      notes: payload.notes || items[index].notes || '',
    };
  } else {
    items.unshift({
      key: `${productId}:${variantId}`,
      productId,
      variantId,
      name: payload.name || 'Item',
      image: payload.image || null,
      priceUnit: Number(payload.priceUnit) || 0,
      originalPrice: Number(
        payload.originalPrice ?? payload.priceUnit ?? 0
      ),
      discount: Boolean(payload.discount),
      discountPercentage: Number(payload.discountPercentage || 0),
      sellingPrice: Number(payload.sellingPrice || payload.priceUnit || 0),
      quantity: ensureQuantity(payload.quantity || 1),
      variantLabel: payload.variantLabel || '',
      colorName: payload.colorName || '',
      sizeName: payload.sizeName || '',
      variants: Array.isArray(payload.variants) ? payload.variants : [],
      notes: payload.notes || '',
      shipping: Number(payload.shipping || 0),
      addedAt: Date.now(),
    });
  }

  return persist({ ...cart, items });
};

export const updateItemQuantity = (productId, variantId, quantity) => {
  const cart = getSession();
  const items = cart.items.map((item) =>
    String(item.productId) === String(productId) &&
    String(item.variantId) === String(variantId)
      ? { ...item, quantity: ensureQuantity(quantity) }
      : item
  );
  return persist({ ...cart, items });
};

export const removeItemFromCart = (productId, variantId) => {
  const cart = getSession();
  const items = cart.items.filter(
    (item) =>
      !(
        String(item.productId) === String(productId) &&
        String(item.variantId) === String(variantId)
      )
  );
  return persist({ ...cart, items });
};

export const clearCart = () => persist({ ...defaultCart });

export const setDeliveryCharge = (amount) => {
  const cart = getSession();
  return persist({
    ...cart,
    deliveryCharge: Number(amount || 0),
  });
};

export const setPromoCode = (promo) => {
  const cart = getSession();
  return persist({
    ...cart,
    promoCode: promo || null,
  });
};
